import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_TEMPLATETRIALITEMS_LOAD,
    ACTION_TEMPLATETRIALITEMS_LOAD_BY_CATEGORY,
    ACTION_TEMPLATETRIALITEMS_LOAD_BY_ID,
    ACTION_TEMPLATETRIALITEMS_LOAD_FAILURE,
    ACTION_TEMPLATETRIALITEMS_LOAD_SUCCESS,

    ACTION_TEMPLATETRIALITEMS_SAVE,
    ACTION_TEMPLATETRIALITEMS_SAVE_SUCCESS,
    ACTION_TEMPLATETRIALITEMS_SAVE_FAILURE,

    ACTION_TEMPLATETRIALITEMS_CREATE,
    ACTION_TEMPLATETRIALITEMS_CREATE_SUCCESS,
    ACTION_TEMPLATETRIALITEMS_CREATE_FAILURE,

    ACTION_TEMPLATETRIALITEMS_CLEAR,
    ACTION_TEMPLATETRIALITEMS_FILTER,

    TemplateTrialItemAction
} from '../actions/templateTrialItems';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";


export interface ITemplateTrialItemState {
    formData: Dtos.TemplateTrialItem | Dtos.TemplateTrialItemCategory[];
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    formRoles: Dtos.RoleType[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[];
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.TemplateTrialItemCategory[];
}

const initialState: ITemplateTrialItemState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    formRoles: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: []
};


function loading(state: ITemplateTrialItemState, isLoading: boolean): ITemplateTrialItemState {
    const newState: ITemplateTrialItemState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: ITemplateTrialItemState,
    formData: Dtos.TemplateTrialItem | Dtos.TemplateTrialItemCategory[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    formRoles: Dtos.RoleType[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ITemplateTrialItemState {
    const newState: ITemplateTrialItemState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        formRoles: {
            $set: formRoles
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: ITemplateTrialItemState, formFilter: IFormFilter): ITemplateTrialItemState {
    
    let formFilteredData: Dtos.TemplateTrialItemCategory[] = [];
        
    const formData: Dtos.TemplateTrialItemCategory[] = state.formData instanceof Array ? state.formData : undefined;
    
    if (!formData) return state;

    // filter the sub items
    formData.forEach(item => {
        let newItem = { ...item };
        let newTemplateTrialItems = [...newItem.templateTrialItems];
        newTemplateTrialItems = newTemplateTrialItems.filter(p =>
            (p.active == true || formFilter.showInActive) &&
            (formFilter.search == "" || p.name.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1 || p.description.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)
        );
        newItem.templateTrialItems = newTemplateTrialItems;
        formFilteredData.push(newItem);
    });

    // filter the categories
    formFilteredData = formFilteredData.filter(p =>
        (p.templateTrialItems.length > 0) ||
        (formFilter.search == "" || p.category.value.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)
    );

    const newState: ITemplateTrialItemState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        }
    });

    return newState;
}

function loadFailure(state: ITemplateTrialItemState, responseStatus: Dtos.ResponseStatus): ITemplateTrialItemState {
    const newState: ITemplateTrialItemState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: ITemplateTrialItemState, propertiesToClear?: string[]): ITemplateTrialItemState {

    if (!propertiesToClear) {
        let formFilter = state.formFilter;
        const newState: ITemplateTrialItemState = update(initialState, {
            formFilter: {
                $set: formFilter
            }
        });
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ITemplateTrialItemState, isSaving: boolean): ITemplateTrialItemState {
    const newState: ITemplateTrialItemState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ITemplateTrialItemState,
    formData: Dtos.TemplateTrialItem | Dtos.TemplateTrialItem[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITemplateTrialItemState {
    const newState: ITemplateTrialItemState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ITemplateTrialItemState, responseStatus: Dtos.ResponseStatus): ITemplateTrialItemState {
    const newState: ITemplateTrialItemState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ITemplateTrialItemState, isCreating: boolean): ITemplateTrialItemState {
    const newState: ITemplateTrialItemState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ITemplateTrialItemState,
    formData: Dtos.TemplateTrialItem,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    formRoles: Dtos.RoleType[],
    lookups: Dtos.Lookup[]
): ITemplateTrialItemState {
    const newState: ITemplateTrialItemState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        formRoles: {
            $set: formRoles
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ITemplateTrialItemState, responseStatus: Dtos.ResponseStatus): ITemplateTrialItemState {
    const newState: ITemplateTrialItemState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

const TemplateTrialItemReducer: Reducer<ITemplateTrialItemState> = (state: ITemplateTrialItemState = initialState, action: TemplateTrialItemAction) => {
    switch (action.type) {
        case ACTION_TEMPLATETRIALITEMS_LOAD_BY_CATEGORY:
        case ACTION_TEMPLATETRIALITEMS_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_TEMPLATETRIALITEMS_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.formRoles, action.lookups, action.responseStatus);
       case ACTION_TEMPLATETRIALITEMS_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_TEMPLATETRIALITEMS_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_TEMPLATETRIALITEMS_SAVE:
            return saving(state, true);
        case ACTION_TEMPLATETRIALITEMS_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TEMPLATETRIALITEMS_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_TEMPLATETRIALITEMS_CREATE:
            return creating(state, true);
        case ACTION_TEMPLATETRIALITEMS_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.formRoles, action.lookups);
        case ACTION_TEMPLATETRIALITEMS_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_TEMPLATETRIALITEMS_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default TemplateTrialItemReducer;